/**
 * Quality tiers. Phones running WebGPU are typically an order of magnitude
 * below a desktop GPU on fill rate, and this renderer is fill-bound (MSAA HDR
 * target plus a per-pixel ray march), so the mobile tier cuts pixels, samples
 * and march steps rather than geometry.
 */

export type TierName = 'mobile' | 'balanced' | 'high';

export interface Quality {
  name: TierName;
  /** MSAA sample count for the scene pass. */
  samples: number;
  /** Post-process antialiasing, when the scene pass is not multisampled. */
  fxaa: boolean;
  /**
   * Temporal antialiasing: jitter the projection and accumulate frames.
   *
   * Implies a single sample — accumulating over time is what replaces
   * multisampling, and paying for both is paying twice for the same edges. It
   * also costs a motion-vector target on the scene pass, which is why it is a
   * flag on the tier and not a switch in the composite.
   */
  taa: boolean;
  /** Hard cap on rendered pixels. */
  maxPixels: number;
  ambientParticles: number;
  dynamicParticles: number;
  /**
   * Ray-march steps in the volumetric pass.
   *
   * Raised when that pass moved to half resolution. The march jitters its start
   * so the steps do not band, which trades banding for speckle, and speckle is
   * variance — it falls as the step count rises. Quartering the pixels bought
   * room to spend on that, and spending it there is what stops the beam looking
   * grainy rather than soft.
   */
  volumetricSteps: number;
  /** Separable blur passes for bloom (must be even to land back in bloomA). */
  blurPasses: number;
  /**
   * Taps on the defocus disc. Too few and the bokeh breaks into a ring of
   * separate points instead of a coin; the count is what buys a solid disc, and
   * it is only ever paid when the player has asked for depth of field.
   */
  dofTaps: number;
  /** Terrain chunks kept resident either side of the camera. */
  chunkRadius: number;
  /** Chunk layers meshed per frame. */
  chunkBudget: number;
  /**
   * Milliseconds of meshing allowed per frame. A single layer can overrun it —
   * the streamer always finishes what it started — but it will not begin
   * another one after the budget is gone, which is what turns a run of
   * expensive chunks into one slow frame instead of five.
   */
  meshBudgetMs: number;
  /**
   * Multiplier on every instance slot's capacity.
   *
   * The capacities in `world.ts` were measured — "peaks for one lane are kelp
   * 1201, coral 997, debris 204, rock 69" — and then doubled for headroom. Two
   * things have happened since. The lanes were POPULATED, with grass, stones
   * and enemies of their own, so the peaks those numbers came from are stale.
   * And the view widens: `speedZoom` and `nitroZoom` open the frame by up to
   * 28%, which is 28% more props asking for room at the exact moment the boat
   * is covering ground fastest.
   *
   * Past capacity, instances are DROPPED SILENTLY and draw order decides who
   * loses — so the far lane, drawn last, is the first thing to go. It does not
   * read as "an instance was dropped", it reads as scenery going black.
   *
   * That budget was identical on a phone and on a workstation. This is the
   * dial that was missing.
   */
  instanceBudget: number;
  /**
   * Dynamic point lights kept in a frame. Past this they are dropped silently,
   * same as instances and with the same draw-order arbitration.
   */
  maxLights: number;
  touchControls: boolean;
}

const TIERS: Record<TierName, Quality> = {
  mobile: {
    name: 'mobile',
    samples: 1,
    // Not TAA on the phone when the player asks for AUTO. It replaces nothing
    // here — this tier was already at one sample — while adding a
    // full-resolution motion target, a full-resolution resolve and a
    // whole-framebuffer copy every frame, and a tile-based mobile GPU pays for
    // bandwidth more dearly than for shading. FXAA is one pass and costs
    // almost nothing.
    //
    // THE SHIPPED DEFAULT IS NO LONGER AUTO. `loadSettings` now hands out
    // 'taa' on every platform, so this line is what a phone falls back to when
    // someone deliberately selects AUTO, not what a phone gets out of the box.
    // The revisit this comment asked for was made on a handset rather than by
    // reasoning, which is the right way round.
    fxaa: true,
    taa: false,
    maxPixels: 900_000,
    // Raised with the mote box pinned: a phone has the fewest motes to start
    // with and the least screen to hide a thin one, so it felt the density
    // arithmetic hardest.
    ambientParticles: 9500,
    dynamicParticles: 4000,
    volumetricSteps: 14,
    blurPasses: 2,
    dofTaps: 12,
    chunkRadius: 3,
    chunkBudget: 1,
    meshBudgetMs: 5,
    // 1.0 is the measured baseline: these capacities were sized on a phone-ish
    // frame and are what the existing numbers already are.
    instanceBudget: 1.0,
    maxLights: 48,
    touchControls: true,
  },
  balanced: {
    name: 'balanced',
    // TAA does its own antialiasing by accumulating frames, so the scene pass
    // rasterises once. That is where the frame time comes from: not a cheaper
    // filter, but three fewer samples of everything.
    samples: 1,
    fxaa: false,
    taa: true,
    maxPixels: 1_600_000,
    ambientParticles: 16000,
    dynamicParticles: 8000,
    volumetricSteps: 24,
    blurPasses: 2,
    dofTaps: 20,
    chunkRadius: 4,
    chunkBudget: 2,
    meshBudgetMs: 9,
    instanceBudget: 1.8,
    maxLights: 96,
    touchControls: false,
  },
  high: {
    name: 'high',
    // TAA does its own antialiasing by accumulating frames, so the scene pass
    // rasterises once. That is where the frame time comes from: not a cheaper
    // filter, but three fewer samples of everything.
    samples: 1,
    fxaa: false,
    taa: true,
    maxPixels: 2_400_000,
    ambientParticles: 24000,
    dynamicParticles: 12000,
    volumetricSteps: 34,
    blurPasses: 4,
    dofTaps: 28,
    chunkRadius: 4,
    chunkBudget: 2,
    meshBudgetMs: 13,
    instanceBudget: 2.6,
    maxLights: 160,
    touchControls: false,
  },
};

export function isTouchDevice(): boolean {
  return (
    (navigator.maxTouchPoints ?? 0) > 0 &&
    window.matchMedia('(pointer: coarse)').matches
  );
}

export function detectQuality(adapter: GPUAdapter | null, prefer: string = 'auto'): Quality {
  const override = new URLSearchParams(location.search).get('q') as TierName | null;
  if (override && TIERS[override]) return { ...TIERS[override] };
  // A saved preference beats detection but loses to the URL, so `?q=` stays
  // the way to check a tier without disturbing what the player chose.
  const touch = isTouchDevice();
  if (prefer !== 'auto' && TIERS[prefer as TierName]) {
    return { ...TIERS[prefer as TierName], touchControls: touch };
  }

  // ANTIALIASING IS OFF UNLESS THE DISPLAY ACTUALLY NEEDS IT.
  //
  // A canvas rendered at device pixel ratio and scaled back down by the panel
  // is already supersampling: at 2x that is four samples a pixel, for free,
  // before any of this runs — which is why a Retina laptop looks clean with
  // every mode off, and why paying for TAA there buys a picture nobody can see.
  //
  // A 1x display has no such margin, and at 1080p or above the pixels are small
  // enough that temporal accumulation is the right answer rather than a
  // filter: it costs a single sample in the scene pass, so it is CHEAPER than
  // the MSAA it replaces as well as quieter on the volumetrics.
  // THAT REASONING HELD WHILE TAA ONLY CLEANED EDGES. It does not any more.
  //
  // TAA is now also the denoiser for every screen-space effect in the frame —
  // occlusion, both shadow marches and reflections all take a few taps at a
  // hashed offset and lean on accumulation to turn that into an integral.
  // Rendering at 2x device pixels does supersample them, but four neighbouring
  // samples is not eight or sixteen frames of history, and it is the history
  // that makes a 10-tap occlusion term look smooth rather than grainy.
  //
  // So it is on by default wherever it can be afforded, Retina or not. The
  // phone tier is the exception below: its screen-space effects are off
  // anyway, so it would be paying for a denoiser with nothing to denoise.
  const wantsTaa = true;
  // A fallback adapter means software rasterisation — never push it.
  const info = adapter?.info as { architecture?: string; isFallbackAdapter?: boolean } | undefined;
  const soft = info?.architecture === 'swiftshader' || !!info?.isFallbackAdapter;

  const aa = { fxaa: false, taa: wantsTaa, samples: 1 };
  // EVERY TOUCH DEVICE STARTS ON THE PHONE TIER, not just the small ones.
  //
  // The gate used to be `touch && screen.min < 820`, which put phones on
  // `mobile` and tablets on `balanced` — an iPad Pro reports 834 and an iPad
  // mini 744, so the line ran straight through the middle of one product
  // family. A tablet is not a laptop with a touchscreen: it is the same
  // passively cooled part in a thinner case, and this game's constraint has
  // never been peak frame cost but the temperature twenty minutes in.
  //
  // It is a DEFAULT and not a lock. Balanced and High are two presses away in
  // the options for anyone whose device deserves them.
  //
  // `samples: 1` rather than the `aa` spread, so the tier keeps the FXAA it
  // declares. Spreading `aa` here set `fxaa: false` and then `taa: false` on
  // top, which left the phone with no antialiasing at all — the opposite of
  // what the tier says two screens up, and by accident of ordering rather
  // than by anyone's decision.
  if (soft || touch) return { ...TIERS.mobile, samples: 1, touchControls: touch };
  return { ...TIERS.high, ...aa };
}

/**
 * Drop a tier when we are persistently missing frame time. Returns null when
 * there is nothing lower to fall back to.
 */
export function lowerTier(q: Quality): Quality | null {
  if (q.name === 'high') return { ...TIERS.balanced, touchControls: q.touchControls };
  if (q.name === 'balanced') return { ...TIERS.mobile, touchControls: q.touchControls };
  return null;
}

/**
 * Apply the player's antialiasing choice over the tier's default.
 *
 * Separate from `detectQuality` because it is a preference rather than a
 * measurement, and because 'auto' has to mean "whatever the tier decided",
 * which is only knowable after detection has run.
 */
export function applyAA(q: Quality, pref: string): Quality {
  if (pref === 'off') return { ...q, samples: 1, fxaa: false, taa: false };
  if (pref === 'fxaa') return { ...q, samples: 1, fxaa: true, taa: false };
  if (pref === 'msaa') return { ...q, samples: 4, fxaa: false, taa: false };
  if (pref === 'taa') return { ...q, samples: 1, fxaa: false, taa: true };
  return q;
}
